import React from 'react';
import { Controller, Control } from 'react-hook-form';
import {
    Autocomplete,
    AutocompleteProps,
    Checkbox,
    TextField,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

interface FilterOptionsState<T> {
    inputValue: string;
    getOptionLabel: (option: T) => string;
}

interface FormAutocompleteProps<T> extends Omit<
    AutocompleteProps<T, true, true, boolean | undefined>,
    'renderInput'
> {
    name: string;
    options: Array<any>;
    label?: string;
    customError?: string;
    control: Control<any>;
    placeholder?: string;
    getOptionDisabled?: (option: any) => boolean;
    filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
}

const FormMultiSelectWithALL = <T,>({
    name,
    control,
    label,
    options,
    customError,
    placeholder,
    ...props
}: FormAutocompleteProps<T>) => {
    const safeGetOptionLabel = (option: any) =>
        typeof option === 'object' && option !== null
            ? option.label || option.value || ''
            : String(option ?? '');

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { onChange, value, ref },
                fieldState: { error },
            }) => {
                const currentValues = Array.isArray(value) ? value : [];

                return (
                    <Autocomplete
                        size='small'
                        getOptionLabel={
                            props.getOptionLabel || safeGetOptionLabel
                        }
                        fullWidth
                        multiple
                        disableCloseOnSelect
                        isOptionEqualToValue={(option: any, value: any) =>
                            option.value === value.value
                        }
                        options={options}
                        value={currentValues}
                        onChange={(_, selectedOptions) => {
                            const cleaned = (selectedOptions || [])
                                .filter((opt: any) => opt?.value !== 'ALL')
                                .map((opt: any) =>
                                    typeof opt === 'string'
                                        ? options.find(
                                              (o) => o.value === opt,
                                          ) || {
                                              value: opt,
                                              label: opt,
                                          }
                                        : opt,
                                );

                            onChange(cleaned);
                        }}
                        renderOption={(props, option, { selected }) => {
                            const optionValue =
                                typeof option === 'object'
                                    ? (option as any).value
                                    : option;
                            const optionLabel =
                                typeof option === 'object'
                                    ? (option as any).label
                                    : option;

                            const isAll = optionValue === 'ALL';
                            const realOptions = options.filter(
                                (o) => o.value !== 'ALL',
                            );

                            const allSelected =
                                currentValues.length === realOptions.length &&
                                realOptions.every((opt) =>
                                    currentValues.some(
                                        (v: any) => v?.value === opt.value,
                                    ),
                                );

                            const isOptionSelected = currentValues.some(
                                (v: any) => v?.value === optionValue,
                            );

                            const label =
                                isAll && allSelected
                                    ? 'Unselect all'
                                    : optionLabel;

                            return (
                                <li
                                    key={optionValue}
                                    {...props}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        if (isAll) {
                                            onChange(
                                                allSelected ? [] : realOptions,
                                            );
                                            return;
                                        }

                                        const exists = currentValues.some(
                                            (v: any) =>
                                                v?.value === optionValue,
                                        );

                                        const updated = exists
                                            ? currentValues.filter(
                                                  (v: any) =>
                                                      v?.value !== optionValue,
                                              )
                                            : [
                                                  ...currentValues,
                                                  typeof option === 'string'
                                                      ? {
                                                            value: optionValue,
                                                            label: optionLabel,
                                                        }
                                                      : option,
                                              ];

                                        onChange(updated);
                                    }}
                                >
                                    <Checkbox
                                        size='small'
                                        sx={{ mr: '8px' }}
                                        checked={
                                            isAll
                                                ? allSelected
                                                : isOptionSelected
                                        }
                                    />
                                    {label}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                inputRef={ref}
                                error={!!error || !!customError}
                                helperText={
                                    (error && error.message) || customError
                                }
                                placeholder={
                                    currentValues && currentValues.length > 0
                                        ? ''
                                        : placeholder
                                }
                            />
                        )}
                        popupIcon={<KeyboardArrowDown />}
                        {...props}
                    />
                );
            }}
        />
    );
};

export default FormMultiSelectWithALL;
